/**
 * Events API Client
 * =================
 * Functions for interacting with the Daily Events and Ticketing APIs.
 */

const BACKEND_URL = window.location.origin;

/**
 * Get daily events for today or this week.
 * @param {Object} options
 * @param {string} options.range - "today" or "week"
 * @param {string} options.type - Filter by "text" or "video" (optional)
 * @param {string} token - Auth token
 * @returns {Promise<Object>} Events response
 */
export async function fetchDailyEvents({ range = "today", type = null } = {}, token) {
  const params = new URLSearchParams();
  params.append("range", range);
  if (type) params.append("type", type);

  const response = await fetch(`${BACKEND_URL}/api/events/daily?${params.toString()}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || "Failed to fetch events");
  }

  return response.json();
}

/**
 * Get a single event by ID.
 * @param {string} eventId
 * @param {string} token - Auth token
 * @returns {Promise<Object>} Event details
 */
export async function fetchEvent(eventId, token) {
  const response = await fetch(`${BACKEND_URL}/api/events/${eventId}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || "Event not found");
  }

  return response.json();
}

/**
 * Join an event.
 * @param {string} eventId
 * @param {string} token - Auth token
 * @returns {Promise<Object>} Join result
 */
export async function joinEvent(eventId, token) {
  const response = await fetch(`${BACKEND_URL}/api/events/${eventId}/join`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    const reason = response.headers.get("X-Reason");
    throw { 
      message: error.detail || "Failed to join event",
      reason: reason || error.reason,
      status: response.status,
    };
  }

  return response.json();
}

/**
 * Get ticket status for an event.
 * @param {string} eventId
 * @param {string} token - Auth token
 * @returns {Promise<Object>} Ticket status including tier
 */
export async function getTicketStatus(eventId, token) {
  const response = await fetch(`${BACKEND_URL}/api/events/${eventId}/tickets/status`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });

  if (!response.ok) {
    return { has_ticket: false };
  }

  return response.json();
}

/**
 * Get ticket pricing options for an event.
 * @param {string} eventId
 * @param {string} token - Auth token
 * @returns {Promise<Object>} Pricing tiers
 */
export async function getTicketPricing(eventId, token) {
  const response = await fetch(`${BACKEND_URL}/api/events/${eventId}/tickets/pricing`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });

  if (!response.ok) {
    throw new Error("Failed to get pricing");
  }

  return response.json();
}

/**
 * Create a ticket checkout session.
 * @param {string} eventId
 * @param {string} token - Auth token
 * @param {Object} options
 * @param {string} options.tier - "standard" or "vip"
 * @param {string} options.successUrl - URL to redirect after successful payment
 * @param {string} options.cancelUrl - URL to redirect after cancelled payment
 * @returns {Promise<Object>} Checkout session with checkout_url
 */
export async function createTicketCheckout(eventId, token, { tier = "standard", successUrl, cancelUrl } = {}) {
  const response = await fetch(`${BACKEND_URL}/api/events/${eventId}/tickets/checkout`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({
      tier,
      success_url: successUrl,
      cancel_url: cancelUrl,
    }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || "Failed to create checkout");
  }

  return response.json();
}

export default {
  fetchDailyEvents,
  fetchEvent,
  joinEvent,
  getTicketStatus,
  createTicketCheckout,
};
